// @flow

import { noop, omit, xor, sortBy } from 'lodash/fp';

import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';
import {
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  API_CALL_REQUEST,
  RESET_SELECTED_CHALLENGE,
  SET_LOBBY_CATEGORY,
  SET_LOBBY_FILTER,
  SET_LOBBY_GROUP_BY,
  SET_LOBBY_SPORT,
  SET_LOBBY_SUB_CATEGORY,
  SET_SELECTED_CHALLENGE,
  TOGGLE_LOBBY_FILTER,
  UNSET_LOBBY_FILTER,
  RESET_LOBBY_FILTER,
  RESET_LOBBY_GROUP_BY,
  RESET_SPORT_CATEGORIES
} from '../constants/actionTypes';
import {
  UPCOMING_CHALLENGES,
  UPCOMING_CUSTOM_CHALLENGES,
  UPCOMING_H2H_CHALLENGES,
  GAMES
} from '../constants/apiTypes';
import decodeChallenge from '../normalizers/decodeChallenge';
import decodeGame from '../normalizers/decodeGame';
import { ALL, FEATURED } from '../constants/challengeCategoryTypes';
import { DATE } from '../constants/groupByTypes';

export type LobbyChallengesState = {
  activeCategory: string,
  activeSport: ?string,
  activeSubCategory: ?string,
  challenges: Array<Object>,
  customChallenges: Array<Object>,
  errors: ?Array<Object>,
  filters: Object,
  groupByKey: string,
  h2hChallenges: Array<Object>,
  isFetching: boolean,
  isCustomFetching: boolean,
  isH2HFetching: boolean,
  isCustomInitialised: boolean,
  isH2HInitialised: boolean,
  isInitialised: boolean,
  selectedChallengeId: ?string
};

const initialState: LobbyChallengesState = {
  activeCategory: ALL,
  activeSport: undefined,
  activeSubCategory: undefined,
  challenges: [],
  errors: null,
  filters: {},
  groupByKey: DATE,
  customChallenges: [],
  h2hChallenges: [],
  isFetching: false,
  isCustomFetching: false,
  isH2HFetching: false,
  isCustomInitialised: false,
  isH2HInitialised: false,
  isInitialised: false,
  selectedChallengeId: null,
  isFetchingGames: false,
  selectedGames: []
};

const apiRequestReducer = switchFunc(
  {
    [UPCOMING_CHALLENGES]: (state, action) => ({
      errors: [],
      isFetching: !action.quiet
    }),
    [UPCOMING_CUSTOM_CHALLENGES]: (state, action) => ({
      errors: [],
      isCustomFetching: !action.quiet
    }),
    [UPCOMING_H2H_CHALLENGES]: (state, action) => ({
      errors: [],
      isH2HFetching: !action.quiet
    }),
    [GAMES]: () => ({
      isFetchingGames: true
    })
  },
  () => noop
);

const apiSuccessReducer = switchFunc(
  {
    [UPCOMING_CHALLENGES]: (state, action) => ({
      challenges: action.data.map(item => decodeChallenge(item, action.meta)),
      isFetching: false,
      isInitialised: true
    }),
    [UPCOMING_CUSTOM_CHALLENGES]: (state, action) => ({
      customChallenges: action.data.map(item => decodeChallenge(item, action.meta)),
      isCustomFetching: false,
      isCustomInitialised: true
    }),
    [UPCOMING_H2H_CHALLENGES]: (state, action) => ({
      h2hChallenges: action.data.map(item => decodeChallenge(item, action.meta)),
      isH2HFetching: false,
      isH2HInitialised: true
    }),
    [GAMES]: (state, action) => ({
      selectedGames: sortBy(
        'startTime',
        action.data.map((g, i) => decodeGame({ ...g, gameOrder: i + 1 }))
      ),
      isFetchingGames: false
    })
  },
  () => noop
);

const apiFailureReducer = switchFunc(
  {
    [UPCOMING_CHALLENGES]: (state, action) => ({
      errors: action.errors,
      isFetching: false
    }),
    [UPCOMING_CUSTOM_CHALLENGES]: (state, action) => ({
      errors: action.errors,
      isCustomFetching: false
    }),
    [UPCOMING_H2H_CHALLENGES]: (state, action) => ({
      errors: action.errors,
      isH2HFetching: false
    })
  },
  () => noop
);

export default createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiRequestReducer(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccessReducer(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailureReducer(action.api)(state, action),
  [SET_LOBBY_SPORT]: (state, action) => ({
    activeSport: action.sport,
    activeSubCategory: action.subCategory
  }),
  [RESET_SPORT_CATEGORIES]: () => ({
    activeSport: undefined,
    activeSubCategory: undefined
  }),
  [SET_LOBBY_SUB_CATEGORY]: (state, action) => ({
    activeSubCategory: action.sport
  }),
  [SET_LOBBY_CATEGORY]: (state, action) => ({
    activeCategory: action.category
  }),
  [SET_LOBBY_FILTER]: (state, action) => ({
    filters: {
      ...state.filters,
      [action.key]: action.value
    }
  }),
  [SET_LOBBY_GROUP_BY]: (state, action) => ({
    groupByKey: action.key
  }),
  [TOGGLE_LOBBY_FILTER]: (state, action) => ({
    filters: omit(action.resetFilters, {
      ...state.filters,
      [action.key]: xor(state.filters[action.key] || [], [action.value])
    })
  }),
  [UNSET_LOBBY_FILTER]: (state, action) => ({
    filters: omit(action.key, state.filters)
  }),
  [RESET_LOBBY_FILTER]: () => ({
    filters: initialState.filters
  }),
  [RESET_LOBBY_GROUP_BY]: () => ({
    groupByKey: initialState.groupByKey
  }),
  [SET_SELECTED_CHALLENGE]: (state, action) => ({
    selectedChallengeId: action.challengeId
  }),
  [RESET_SELECTED_CHALLENGE]: () => ({
    selectedChallengeId: null,
    selectedGames: [],
    isFetchingGames: false
  })
});
